/* global JSX */
import { Alert, Box, Chip, Stack } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { ToolTitle } from "../../framework/Tool/ToolTitle";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { SelectUnitDialog } from "source/components/framework/Unit/SelectUnitDialog";
import { RemoveToolButton } from "source/components/framework/Button/RemoveToolButton";
import { AddToolButton } from "source/components/framework/Button/AddToolButton";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { AppUtils } from "source/framework/appUtils";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";

//UnitBlockItem
interface IUnitBlockItemProps {
    unit: IIdTitleNode;
    selectedIds: string[];
    onClick: (id: string) => void;
}

export const UnitBlockItem = (props: IUnitBlockItemProps) => {
    const unit = props.unit;
    const selected = props.selectedIds.indexOf(unit.id) > -1;
    return (
        <Chip
            label={unit.title}
            avatar={<AvatarImage
                id={unit.title}
                marginLeft={"4px"}
                name={unit.title}
                // image={unit.image}
                tooltip={unit.title}
            />}
            variant={selected ? "filled" : "outlined"}
            onClick={(e: { stopPropagation: () => void; }) => { e.stopPropagation(); props.onClick(unit.id); }}
        />
    );
};

//UnitBlocks
export interface IUnitBlocksProp {
    isReadOnly?: boolean;
    title: string | JSX.Element;
    noUnitsMessage?: string;
    parentIsNew?: boolean;
    parentIsNewMessage?: string;
    unitTree: IIdTitleNode[];
    margin?: string | number;
    units: IIdTitleNode[];
    showInPanel?: boolean;
    height?: number | string;
    onAddUnit: (unit: IIdTitleNode) => Promise<ApiSuccess>;
    onRemoveUnits: (unitIds: string[]) => Promise<ApiSuccess>;
}

interface IUnitBlocksState {
    unitTree: IIdTitleNode[];
    selectedIds: string[];
    showSelectUnitDialog: boolean;
}

export class UnitBlocks extends React.Component<IUnitBlocksProp, IUnitBlocksState> {
    constructor(props: IUnitBlocksProp) {
        super(props);
        const unitTree = Base.getTypedArray(props.unitTree, IdTitleNode);
        const selectedUnits = IdTitleNode.getByIds(unitTree, props.units.map(i => i.id));
        for (const unit of selectedUnits) {
            unit.active = false;
        }
        this.state = {
            unitTree,
            selectedIds: [],
            showSelectUnitDialog: false
        };
    }

    componentDidUpdate(prevProps: IUnitBlocksProp, prevState: IUnitBlocksState) {
        const props = this.props;
        const state = this.state;
        const prevHash = JSON.stringify(prevProps.units) + "#" + JSON.stringify(prevProps.unitTree);
        const hash = JSON.stringify(props.units) + "#" + JSON.stringify(props.unitTree);
        if (prevHash === hash) return;
        const unitTree = Base.getTypedArray(props.unitTree, IdTitleNode);
        const selectedUnits = IdTitleNode.getByIds(unitTree, props.units.map(i => i.id));
        for (const unit of selectedUnits) {
            unit.active = false;
        }
        const unitIds = props.units.map(i => i.id);
        this.setState({
            unitTree,
            selectedIds: state.selectedIds.filter(i => unitIds.indexOf(i) > -1)
        });
    }

    handleBlockClick = (id: string) => {
        this.setState({
            selectedIds: [id]
        });
    };

    // #region Add Unit
    handleAdd = () => {
        this.setState({
            showSelectUnitDialog: true
        });
    };

    handleSelectUnitDialogOk = async (unit: IIdTitleNode) => {
        const props = this.props;
        if (!unit) return;
        const result = await AppUtils.callService(() => props.onAddUnit(new IdTitleNode(unit)));
        if (!result) return;
        this.setState({
            showSelectUnitDialog: false
        });
    };

    handleSelectUnitDialogCancel = () => {
        this.setState({
            showSelectUnitDialog: false
        });
    };
    // #endregion Add Unit

    // #region Remove Unit
    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        if (state.selectedIds.length < 1) return;
        await AppUtils.callService(() => props.onRemoveUnits(state.selectedIds));
    };
    // #endregion Remove Unit

    getMessagesElement = () => {
        const props = this.props;
        const units = props.units;
        const showParentIsNewMessage = !props.isReadOnly && !!props.parentIsNew && !!props.parentIsNewMessage;
        const shownNUnitsMessage = !props.isReadOnly && units.length < 1 && !!props.noUnitsMessage;
        if (showParentIsNewMessage || shownNUnitsMessage) {
            return (<Box sx={{ margin: "-16px" }}>
                {showParentIsNewMessage &&
                    <Alert severity="info" sx={{ width: "100%" }}>{props.parentIsNewMessage}</Alert>
                }
                {shownNUnitsMessage &&
                    <Alert severity="info" sx={{ width: "100%" }}>{props.noUnitsMessage}</Alert>
                }
            </Box>);
        }
        return null;
    };

    getBlocksElement = () => {
        const props = this.props;
        const state = this.state;
        const units = props.units;
        return (<Stack direction="row" spacing={1} sx={{ marginTop: ".5rem" }} useFlexGap flexWrap="wrap">
            {units.map((unit) =>
                <UnitBlockItem
                    key={unit.id}
                    unit={unit}
                    selectedIds={state.selectedIds}
                    onClick={this.handleBlockClick}
                />
            )}
        </Stack>);
    };

    render() {
        const props = this.props;
        const state = this.state;
        const titleIsString = Base.isString(props.title);
        const messagesElement = this.getMessagesElement();
        const blocksDataElement = messagesElement ? null : this.getBlocksElement();
        return (
            <>
                {(props.showInPanel
                    ? <DialogSectionCard
                        headerTitle={props.title}
                        buttons={
                            <ListIconButtonsItem
                                addTooltip={Translations.AddUnit}
                                removeTooltip={Translations.RemoveUnit}
                                removeDisabled={state.selectedIds.length < 1}
                                onAdd={!props.isReadOnly ? this.handleAdd : null}
                                onRemove={!props.isReadOnly ? this.handleRemove : null}
                            />
                        }
                    >
                        {(messagesElement
                            ? <Box sx={{ height: `calc(${props.height} - .5rem - .2rem)` }}>
                                {messagesElement}
                            </Box>
                            : <Box sx={{ margin: "-1rem", padding: ".5rem", height: "calc(100% + 40px)", overflowY: "auto", overflowX: "hidden" }}>
                                {blocksDataElement}
                            </Box>
                        )}
                    </DialogSectionCard>
                    : <>
                        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ margin: props.margin ?? "1.5rem 0 0 0" }}>
                            {titleIsString &&
                                <ToolTitle
                                    title={props.title}
                                />
                            }
                            {!titleIsString &&
                                props.title
                            }
                            <Stack direction="row">
                                {!props.isReadOnly &&
                                    <AddToolButton onClick={this.handleAdd} tooltip={Translations.AddUnit} disabled={props.parentIsNew} />
                                }
                                {!props.isReadOnly &&
                                    <RemoveToolButton onClick={this.handleRemove} tooltip={Translations.RemoveUnit} disabled={state.selectedIds.length < 1} />
                                }
                            </Stack>
                        </Box>
                        {messagesElement || blocksDataElement}
                    </>
                )}
                {state.showSelectUnitDialog &&
                    <SelectUnitDialog
                        hideDisabledIcon={true}
                        nodes={state.unitTree}
                        title={Translations.SelectUnit}
                        firstColumnTitle={Translations.Unit}
                        selectedId={""}
                        onOk={this.handleSelectUnitDialogOk}
                        onCancel={this.handleSelectUnitDialogCancel}
                    />
                }
            </>
        );
    }
}